import { useCallback, useState } from 'react';
import { closeSprint, type CloseSprintResult } from '../../api/endpoints';
import { toApiError } from '../../api/errors';
import type { Id, Sprint } from '../../api/types';
import { describeCarryOverResult } from './sprint-close';

/**
 * Completing a sprint — the one write this screen makes that cannot be undone.
 *
 * 🔴 CONFIRMATION IS A STATE, NOT A `window.confirm`. The dialog has to say what will happen to the
 *   unfinished tickets and let the user choose where they go, and neither fits in a browser confirm.
 *   ⇒ `asking` holds the sprint under consideration; nothing is sent until `confirm` is called.
 *
 * ⛔ THE ERROR IS THE API'S OWN WORDS, exactly as `useRankReorder` handles its 409. `reason` FIRST:
 *   for this endpoint that is `a 'closed' sprint cannot be closed; only an active sprint can`, or
 *   `the carry-over target sprint is closed; pick an open sprint or the backlog`, or
 *   `only an active member of this project may modify its sprints`. Every one of those tells the
 *   reader something "Could not complete the sprint" does not — and the first of them is what a
 *   second person closing the sprint first looks like.
 *
 * ⭐ THE DIALOG STAYS OPEN ON FAILURE. Nothing changed, the user's choice of destination is still
 *   selected, and the refusal is shown against the button that caused it. Closing the dialog and
 *   dropping a banner elsewhere would lose the context and the choice at the same time.
 */
export interface CompleteSprintState {
  /** The sprint the confirmation is open for, or `null`. */
  asking: Sprint | null;
  busy: boolean;
  /** The API's own explanation of a refusal. */
  error: string | null;
  /** What the API reported it did, once it has succeeded. */
  outcome: string | null;
  ask: (sprint: Sprint) => void;
  cancel: () => void;
  dismissOutcome: () => void;
  /** `null` destination means the backlog — see `closeSprint`. */
  confirm: (carryOverToSprintId: Id | null) => void;
}

export function useCompleteSprint(
  onCompleted: (result: CloseSprintResult) => void,
): CompleteSprintState {
  const [asking, setAsking] = useState<Sprint | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  const ask = useCallback((sprint: Sprint) => {
    setAsking(sprint);
    setError(null);
    setOutcome(null);
  }, []);

  const cancel = useCallback(() => {
    // ⛔ Not while a request is in flight: the sprint may already be closed by the time the dialog
    //   would disappear, and dismissing the only place the answer can appear is how a write becomes
    //   silent.
    /**
     * ⛔ Not while a request is in flight, and the reason is specific: if the dialog is dismissed
     *   mid-write, a FAILURE has nowhere to appear — `setError` would write to a panel that is no
     *   longer mounted and the refusal would be silent.
     * ⚠️ MEASURED, NOT ASSUMED: breaking this guard alone does NOT turn the suite red, because the
     *   Cancel button is `disabled` while busy and the path is unreachable through the UI. It is
     *   kept as the second line — the `disabled` attribute is the one under test.
     */
    setBusy((inFlight) => {
      if (!inFlight) {
        setAsking(null);
        setError(null);
      }
      return inFlight;
    });
  }, []);

  const confirm = useCallback(
    (carryOverToSprintId: Id | null) => {
      const sprint = asking;
      if (!sprint || busy) return;

      setBusy(true);
      setError(null);

      void (async () => {
        try {
          const result = await closeSprint(sprint.id, carryOverToSprintId);
          setAsking(null);
          // ⭐ The SERVER's count of what moved, not our pre-flight estimate — which was allowed to be
          //   a floor. What happened is not an estimate.
          setOutcome(describeCarryOverResult(result));
          onCompleted(result);
        } catch (caught) {
          const apiError = toApiError(caught, `/sprints/${sprint.id}/close`);
          setError(apiError.reason ?? apiError.message);
        } finally {
          setBusy(false);
        }
      })();
    },
    [asking, busy, onCompleted],
  );

  return {
    asking,
    busy,
    error,
    outcome,
    ask,
    cancel,
    dismissOutcome: () => setOutcome(null),
    confirm,
  };
}
